import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import {
  PatientTimelineService,
  PatientTimelineItem,
  PatientTimelineEventType,
} from "../../api/patientTimeline";
import { getAllPatients } from "../../api/patient";

interface PatientOption {
  id: string;
  name: string;
}

const PatientTimelineList: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<PatientTimelineItem[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string>("");

  useEffect(() => {
    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const res = await getAllPatients();
        const list = res?.data?.data || res?.data || [];
        const normalized: PatientOption[] = (Array.isArray(list) ? list : [])
          .map((p: any) => {
            const id = p.id || p._id || p.patientId || p.value || "";
            const name =
              p.name ||
              `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
              p.label ||
              "";
            return { id, name };
          })
          .filter((p) => p.id && p.name);
        setPatients(normalized);
      } catch (err: any) {
        setPatients([]);
      } finally {
        setPatientsLoading(false);
      }
    };

    fetchPatients();
  }, []);

  const fetchTimeline = async (patientId: string) => {
    if (!patientId) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      const res = await PatientTimelineService.getByPatientId(patientId);
      const raw = res?.data?.data || res?.data || [];
      const list: PatientTimelineItem[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw.items)
          ? raw.items
          : [];
      setItems(list);
    } catch (err: any) {
      setItems([]);
      toast.error(
        err?.response?.data?.message ||
        "Failed to load patient timeline records."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePatientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedPatientId(value);
    fetchTimeline(value);
  };

  const formatDate = (item: PatientTimelineItem | any) => {
    const created =
      item.createdOn ||
      item.createdAt ||
      item.meta?.createdAt ||
      item.meta?.updatedAt;
    if (!created) return "";
    const d = new Date(created);
    if (Number.isNaN(d.getTime())) return created;
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const getEventLabel = (eventType: PatientTimelineEventType | string) => {
    switch (eventType) {
      case "EXAM_ADDED":
        return "Patient Examination Added";
      case "TREATMENT_ADDED":
        return "Treatment Added";
      case "APPOINTMENT_CREATED":
        return "Appointment Created";
      case "APPOINTMENT_MISSED":
        return "Appointment Missed";
      case "PAYMENT_ADDED":
        return "Payment Added";
      default:
        return eventType;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {(loading || patientsLoading) && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              {/* Header */}
              <div className="mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Patient Timeline
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Select a patient to view their treatment and appointment events.
                </p>
              </div>

              {/* Patient Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Patient
                </label>
                <select
                  value={selectedPatientId}
                  onChange={handlePatientChange}
                  disabled={patientsLoading}
                  className="w-full md:w-80 p-2 border rounded-lg"
                >
                  {patientsLoading ? (
                    <option value="">Loading patients...</option>
                  ) : (
                    <>
                      <option value="">Select Patient</option>
                      {patients.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {/* Timeline */}
              {!selectedPatientId ? (
                <div className="py-10 text-center text-gray-500">
                  Please select a patient to view their timeline.
                </div>
              ) : items.length === 0 ? (
                <div className="py-10 text-center text-gray-500">
                  No timeline events found for the selected patient.
                </div>
              ) : (
                <div className="relative pl-6 border-l-2 border-dashed border-[#016683]/30">
                  {items.map((item, index) => {
                    const isMissed = item.eventType === "APPOINTMENT_MISSED";
                    const label = getEventLabel(item.eventType);
                    const dateLabel = formatDate(item);

                    // try to surface a short meta description if available
                    const notes =
                      (item as any).meta?.treatmentNotes ||
                      (item as any).meta?.wireChange ||
                      "";
                    const shortNotes =
                      typeof notes === "string" && notes.length > 0
                        ? notes.length > 90
                          ? notes.slice(0, 90) + "..."
                          : notes
                        : "";

                    return (
                      <div
                        key={item.id || index}
                        className="relative mb-5 last:mb-0"
                      >
                        <div className="absolute -left-3 top-4 w-3 h-3 rounded-full bg-[#016683]" />
                        <div className="ml-4 rounded-2xl border border-gray-100 bg-gradient-to-r from-white to-gray-50 px-4 py-3 shadow-sm">
                          <p
                            className={`text-sm font-semibold ${isMissed ? "text-red-500" : "text-gray-800"
                              }`}
                          >
                            {label}
                          </p>
                          {dateLabel && (
                            <p className="text-xs text-[#00A0D2] mt-1">
                              {dateLabel}
                            </p>
                          )}
                          {shortNotes && (
                            <p className="mt-1 text-xs text-gray-500">
                              {shortNotes}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default PatientTimelineList;


